"use client";

import React, { useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { 
  UtensilsCrossed, 
  Globe, 
  KanbanSquare, 
  RotateCcw, 
  ArrowLeft, 
  Sparkles,
  Check
} from "lucide-react";

interface RestaurantTopBarProps {
  currency?: "INR" | "USD";
  onCurrencyChange?: (c: "INR" | "USD") => void;
  onResetComplete?: () => void;
}

export default function RestaurantTopBar({
  currency = "INR",
  onCurrencyChange,
  onResetComplete,
}: RestaurantTopBarProps) {
  const pathname = usePathname();
  const isCrm = pathname.includes("/crm");
  const [isResetting, setIsResetting] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  const handleReset = async () => {
    try {
      setIsResetting(true);
      const res = await fetch("/api/demos/restaurant/reset", { method: "POST" });
      if (res.ok) {
        setResetSuccess(true);
        if (onResetComplete) onResetComplete();
        setTimeout(() => setResetSuccess(false), 2500);
      }
    } catch (err) {
      console.error("Failed to reset restaurant demo:", err);
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <div className="sticky top-0 z-50 w-full bg-stone-950/95 text-stone-200 border-b border-amber-600/20 backdrop-blur-xl shadow-2xl transition-all">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2.5 flex flex-wrap items-center justify-between gap-3">
        {/* Left: Demo Indicator */}
        <div className="flex items-center gap-2.5">
          <Link
            href="/demos"
            className="flex items-center gap-1 text-xs text-stone-400 hover:text-amber-400 transition-colors mr-1 group"
          >
            <ArrowLeft className="w-3.5 h-3.5 group-hover:-translate-x-0.5 transition-transform" />
            <span className="hidden md:inline">All Demos</span>
          </Link>

          <div className="h-4 w-px bg-stone-800 hidden md:block" />

          <div className="flex items-center gap-2">
            <span className="flex h-2 w-2 rounded-full bg-amber-500 animate-pulse" />
            <span className="text-[11px] font-semibold tracking-wider uppercase text-amber-500/90 flex items-center gap-1">
              <Sparkles className="w-3 h-3 text-amber-400" />
              Vertical Demo
            </span>
            <span className="hidden sm:inline-block text-xs font-bold text-amber-200 bg-amber-500/10 px-2.5 py-0.5 rounded-full border border-amber-500/30">
              🍽️ Restaurant & Fine Dining CRM
            </span>
          </div>
        </div>

        {/* Center: Switcher between Client Website & Reservation CRM */}
        <div className="flex items-center bg-stone-900/90 p-1 rounded-lg border border-stone-800 shadow-inner">
          <Link
            href="/demos/restaurant"
            className={`flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-md transition-all ${
              !isCrm
                ? "bg-gradient-to-r from-amber-600 to-amber-700 text-white shadow-sm"
                : "text-stone-400 hover:text-white"
            }`}
          >
            <Globe className="w-3.5 h-3.5" />
            <span>Client Website</span>
          </Link>
          <Link
            href="/demos/restaurant/crm"
            className={`flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-md transition-all ${
              isCrm
                ? "bg-gradient-to-r from-amber-600 to-amber-700 text-white shadow-sm"
                : "text-stone-400 hover:text-white"
            }`}
          >
            <KanbanSquare className="w-3.5 h-3.5 text-amber-300" />
            <span>Floor & Reservation CRM</span>
          </Link>
        </div>

        {/* Right: Currency Toggle & Reset Demo Data */}
        <div className="flex items-center gap-2">
          {onCurrencyChange && (
            <div className="flex items-center bg-stone-900 border border-stone-800 rounded-lg p-0.5 text-xs font-medium">
              <button
                onClick={() => onCurrencyChange("INR")}
                className={`px-2 py-0.5 rounded transition-all ${
                  currency === "INR"
                    ? "bg-amber-600 text-white font-bold"
                    : "text-stone-400 hover:text-white"
                }`}
              >
                ₹ INR
              </button>
              <button
                onClick={() => onCurrencyChange("USD")}
                className={`px-2 py-0.5 rounded transition-all ${
                  currency === "USD"
                    ? "bg-amber-600 text-white font-bold"
                    : "text-stone-400 hover:text-white"
                }`}
              >
                $ USD
              </button>
            </div>
          )}

          <button
            onClick={handleReset}
            disabled={isResetting}
            title="Reset demo reservations to original state"
            className="flex items-center gap-1 text-[11px] font-medium text-stone-400 hover:text-white bg-stone-900/80 hover:bg-stone-800 px-2.5 py-1 rounded-lg border border-stone-800 transition-all disabled:opacity-50"
          >
            {resetSuccess ? (
              <>
                <Check className="w-3 h-3 text-emerald-400" />
                <span className="text-emerald-400 font-semibold">Reset Done</span>
              </>
            ) : (
              <>
                <RotateCcw className={`w-3 h-3 ${isResetting ? "animate-spin text-amber-400" : ""}`} />
                <span className="hidden sm:inline">Reset Demo</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
